import React from "react";

import { useTable } from "@refinedev/react-table";
import { useQuery } from "@tanstack/react-query";
import { Loader2, RefreshCw } from "lucide-react";

import { DataTable } from "@/components/refine-ui/data-table/data-table";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  VENUE_AUDIT_KIND_HINT,
  VENUE_AUDIT_SEVERITY_OPTIONS,
  VENUE_AUDIT_STATUS_OPTIONS,
  isVenueAuditKind,
  isVenueAuditSeverity,
  isVenueAuditStatus,
} from "@/lib/tina/venue-audit";
import { DismissFindingDialog } from "@/pages/tina/audits/venue/components/dismiss-finding-dialog";
import { KindCountStrip } from "@/pages/tina/audits/venue/components/kind-count-strip";
import { useVenueAuditColumns } from "@/pages/tina/audits/venue/hooks/use-venue-audit-columns";
import {
  VENUE_AUDIT_QUERY_ROOT,
  useVenueAuditRepository,
} from "@/pages/tina/audits/venue/hooks/use-venue-audit-repository";
import {
  type VenueAuditFilterState,
  buildVenueAuditFilters,
} from "@/pages/tina/audits/venue/lib/venue-audit-filters";
import { usePersistedState } from "@/pages/tina/hooks/use-persisted-state";
import type { VenueAuditFindingRow, VenueAuditRunResponse } from "@/types/tina";

const ANY_SEVERITY = "__any_severity__";

/**
 * The Venue tab of `/tina/audits` — the venue cache auditor's review queue.
 *
 * **What it is for.** Tina binds a scraped event to a Mood venue record, and two
 * investigations on 2026-09-07 found whole families of wrong binds that nothing detected:
 * one cost 7 published events, one cost 796. Nine deterministic checks now run daily over
 * `site_venue_cache` and a read of the Mood venue catalog, and write what they find to
 * `venue_audit_findings`. This page is where a curator reads those findings.
 *
 * **Dismissal is the only action.** There is no repair here: fixing a wrong bind means
 * editing the venue in Mood, which is what every venue link in the Finding column opens.
 * Dismissing records that a curator looked at a finding and accepted it, permanently —
 * until the audit sees a materially different version of it.
 *
 * **Why the count strip.** The first real backlog is ~178 findings, ~123 of them a single
 * kind. A mixed pager over that tells a curator nothing about the work ahead; nine server
 * counts do, and each is one click into a queue of a single question, with that question
 * spelled out under the strip. `status` and `severity` narrow across all of them, so "the
 * 36 low-severity CID collisions" is two clicks.
 *
 * All narrowing is server-side: `status`, `kind` and `severity` are the only three params
 * `GET /venue-audit/findings` takes, they go in as `permanent` filters, and `total` is
 * counted over the same WHERE the page is — so no column here carries a filter of its own.
 */
export const VenueAuditTab = () => {
  const repo = useVenueAuditRepository();

  const [persistedStatus, setStatus] = usePersistedState<string | null>(
    "tina-venue-audit:status",
    "open",
  );
  const [persistedKind, setKind] = usePersistedState<string | null>(
    "tina-venue-audit:kind",
    null,
  );
  const [persistedSeverity, setSeverity] = usePersistedState<string | null>(
    "tina-venue-audit:severity",
    null,
  );
  // Narrowed on read rather than trusted: these come back from localStorage, where a
  // kind this build no longer knows can be sitting, and all three params are typed
  // Literals on the route — an unknown value is a 422, not an empty page.
  const status = isVenueAuditStatus(persistedStatus) ? persistedStatus : "open";
  const kind = isVenueAuditKind(persistedKind) ? persistedKind : null;
  const severity = isVenueAuditSeverity(persistedSeverity)
    ? persistedSeverity
    : null;

  const [pendingDismissal, setPendingDismissal] =
    React.useState<VenueAuditFindingRow | null>(null);
  const [dismissingId, setDismissingId] = React.useState<string | null>(null);
  const [runOutcome, setRunOutcome] =
    React.useState<VenueAuditRunResponse | null>(null);
  const [running, setRunning] = React.useState(false);

  const onDismiss = React.useCallback((row: VenueAuditFindingRow) => {
    setPendingDismissal(row);
  }, []);
  const columns = useVenueAuditColumns({ onDismiss, dismissingId });

  const filterState: VenueAuditFilterState = { status, kind, severity };
  const table = useTable<VenueAuditFindingRow>({
    columns,
    refineCoreProps: {
      // Resolved by `RESOURCE_PATHS` to `/venue-audit/findings` — the collection
      // lives a segment deeper than the resource name, beside `/venue-audit/run`.
      resource: "tina-venue-audit",
      pagination: { pageSize: 50 },
      filters: { permanent: buildVenueAuditFilters(filterState) },
    },
  });

  const setCurrentPage = table.refineCore.setCurrentPage;
  const total = table.refineCore.tableQuery.data?.total;

  // Reset to page 1 whenever the narrowing changes, so a pick never strands the table
  // on an out-of-range page of a smaller result.
  const previousScope = React.useRef(`${status}|${kind}|${severity}`);
  React.useEffect(() => {
    const scope = `${status}|${kind}|${severity}`;
    if (previousScope.current === scope) return;
    previousScope.current = scope;
    setCurrentPage(1);
  }, [status, kind, severity, setCurrentPage]);

  const countsQuery = useQuery({
    queryKey: [VENUE_AUDIT_QUERY_ROOT, "counts", status, severity],
    queryFn: () => repo.countsByKind({ status, severity }),
  });

  async function confirmDismissal(note: string): Promise<void> {
    const finding = pendingDismissal;
    if (!finding) return;
    setDismissingId(finding.id);
    try {
      const result = await repo.dismiss(finding.id, note.trim() || null);
      // `null` is the "identity has not loaded" refusal — the repo has already said
      // so, and keeping the dialog open lets the curator retry after a reload.
      if (result) setPendingDismissal(null);
    } catch {
      // The repository surfaced it; the dialog stays open so nothing is lost.
    } finally {
      setDismissingId(null);
    }
  }

  async function onRunNow(): Promise<void> {
    if (running) return;
    setRunning(true);
    try {
      setRunOutcome(await repo.runNow());
      await countsQuery.refetch();
    } catch {
      // Surfaced by the repository.
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">
            Venue audit
            {typeof total === "number" ? (
              <span className="ml-2 font-mono text-base tabular-nums text-muted-foreground">
                {total}
              </span>
            ) : null}
          </h2>
          <p className="max-w-3xl text-sm text-muted-foreground">
            A dismissed finding comes back only if the venues involved change,
            or if it gets worse.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={status} onValueChange={(value) => setStatus(value)}>
            <SelectTrigger className="h-9 w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {VENUE_AUDIT_STATUS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={severity ?? ANY_SEVERITY}
            onValueChange={(value) =>
              setSeverity(value === ANY_SEVERITY ? null : value)
            }
          >
            <SelectTrigger className="h-9 w-[160px]">
              <SelectValue placeholder="Any severity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY_SEVERITY}>Any severity</SelectItem>
              {VENUE_AUDIT_SEVERITY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            className="h-9"
            onClick={() => void onRunNow()}
            disabled={running}
            title="Starts the audit job: it re-reads the Mood venue catalog and re-runs every check. Writes findings only, never a venue. Takes a couple of minutes, and this page does not update on its own — reload it when the run is done."
          >
            {running ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Run audit now
          </Button>
        </div>
      </div>

      <KindCountStrip
        counts={countsQuery.data}
        isLoading={countsQuery.isLoading}
        selected={kind}
        onSelect={(next) => setKind(next)}
      />

      <p className="max-w-4xl text-xs text-muted-foreground">
        {kind
          ? VENUE_AUDIT_KIND_HINT[kind]
          : "Pick a kind to work one question at a time. High severity means provably wrong, or too far apart to be one place."}
      </p>

      {runOutcome ? (
        <div
          className={
            runOutcome.kind === "dispatched"
              ? "rounded-md border bg-muted/40 p-3 text-sm"
              : "rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm"
          }
        >
          {runOutcome.kind === "dispatched" ? (
            // STARTED, not finished — and the banner has to say which. The audit runs in
            // a Cloud Run Job now, so this page cannot know its counts; claiming any
            // here would be inventing them. The findings table below is the result, once
            // the run has had its couple of minutes.
            <span>
              <strong className="font-medium">Audit started.</strong> It reads
              the whole Mood venue catalog and re-runs every check, which
              takes a couple of minutes. Nothing on this page updates on its own
              — reload it when the run is done.
            </span>
          ) : (
            <span>
              <strong className="font-medium">
                {runOutcome.kind === "noop"
                  ? "Nothing started."
                  : "The audit could not be started."}
              </strong>{" "}
              {runOutcome.kind === "noop"
                ? "The audit job is not configured on this environment."
                : (runOutcome.error_message ??
                  "Cloud Run refused and gave no reason.")}
            </span>
          )}
        </div>
      ) : null}

      <DataTable table={table} />

      <DismissFindingDialog
        finding={pendingDismissal}
        pending={dismissingId !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDismissal(null);
        }}
        onConfirm={(note) => void confirmDismissal(note)}
      />
    </div>
  );
};
